import React, { createContext, useContext, useMemo, useState, useRef, useEffect } from 'react';
import { noop, pick, keyBy, map, filter } from 'lodash';
import PropTypes from 'prop-types';
import { useFormDataContext } from 'modules/taboola-common-frontend-modules/formData';
import { useSegmentTypeLimits } from '../../common-campaign-form/components/Tree/hooks/useSegmentTypeLimits';
import { BULK_OPERATION_TYPE } from '../../sponsored-campaign-form/const/bulkOperationType';
import { useEntityFormIndications } from '../layouts/BulkEditLayout/hooks/useEntityFormIndications';
import { useGridRowSelectorHandlers } from '../layouts/BulkEditLayout/hooks/useGridRowSelectorHandlers';

export const BulkOperationsContext = createContext({
    operationType: BULK_OPERATION_TYPE.REPLACE,
    setOperationType: noop,
    setSelectedEntityIds: noop,
});

export const BulkOperationsProvider = ({
    children,
    entityConfig,
    initialOperationType = BULK_OPERATION_TYPE.REPLACE,
    entityIds,
}) => {
    const { data, setData } = useFormDataContext();
    const [operationType, setOperationType] = useState(initialOperationType);
    const selectedEntityIds = useMemo(() => {
        const selectedData = filter(data, entity => Boolean(entity.isSelected));
        return map(selectedData, 'id');
    }, [data]);
    const previewSectionRef = useRef();
    const [editedEntityIds, setEditedEntityIds] = useState([]);
    const { indications, setIndications, addToIndications } = useEntityFormIndications();
    const { selectEntity, unselectEntity, selectAllEntities, unselectAllEntities } = useGridRowSelectorHandlers({
        setData,
    });
    const { maxTargetings } = useSegmentTypeLimits();

    const entityDataByEntityIdMap = useMemo(() => keyBy(data, 'id'), [data]);

    const selectedEntityIdsMap = useMemo(
        () => pick(entityDataByEntityIdMap, selectedEntityIds),
        [entityDataByEntityIdMap, selectedEntityIds]
    );

    const [sitesTargetingTypeOverride, setSitesTargetingTypeOverride] = useState(null);

    useEffect(() => {
        selectAllEntities();
    }, [selectAllEntities]);

    const value = useMemo(
        () => ({
            operationType,
            setOperationType,
            selectedEntityIds,
            selectedEntityIdsMap,
            entityConfig,
            previewSectionRef,
            editedEntityIds,
            setEditedEntityIds,
            sitesTargetingTypeOverride,
            setSitesTargetingTypeOverride,
            indications,
            setIndications,
            addToIndications,
            entityIds,
            selectEntity,
            unselectEntity,
            selectAllEntities,
            unselectAllEntities,
            maxTargetings,
        }),
        [
            operationType,
            selectedEntityIds,
            selectedEntityIdsMap,
            entityConfig,
            editedEntityIds,
            sitesTargetingTypeOverride,
            indications,
            setIndications,
            addToIndications,
            entityIds,
            selectEntity,
            unselectEntity,
            selectAllEntities,
            unselectAllEntities,
            maxTargetings,
        ]
    );

    return <BulkOperationsContext.Provider value={value}>{children}</BulkOperationsContext.Provider>;
};

BulkOperationsProvider.propTypes = { children: PropTypes.node };

export const useBulkOperationsContext = () => useContext(BulkOperationsContext);
